import React, { useState, useRef, useEffect } from 'react'

import Form from 'components/form/form'
import copy from 'fast-copy'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'

import {
    getCurrentUser,
    getCurrentAccount,
} from 'state/selectors'

import {
    saveAccount,
    loginUser,
    updateUserSubscriber
} from 'state/actions'

import yup from 'yup'

import { logAnalyticsEvent } from 'utils/analytics'

import { 
    getMobileOperatingSystem
} from 'utils'

import {
	isCordova
} from 'env'

const Account = ({
    setStep,
    isActiveStep,
    userData,
    accountData,
    saveAccount,
    refreshSlider,
    loginUser,
    history,
    loginCredentials,
    updateUserSubscriber,
    isSecondaryAccount,
    appSumoUsers,
    appSumoCode,
    closeCallback
}) => {
    const currentUserRef = useRef(userData)
    currentUserRef.current = userData

    const isActiveStepRef = useRef(isActiveStep)
    isActiveStepRef.current = isActiveStep

    const [formValues, setFormValues] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const formValuesRef = useRef(formValues)
    formValuesRef.current = formValues

    const [formError, setFormError] = useState(null)

    const industries = [
        {
            value: 'CREATIVE',
            label: 'Creative'
        },
        {
            value: 'HR',
            label: 'HR/Recruitment'
        },
        {
            value: 'MARKETING',
            label: 'Marketing'
        },
        {
            value: 'PRODUCT_DEVELOPMENT',
            label: 'Product Development'
        }, {
            value: 'OTHER',
            label: 'Something else'
        }
    ]

    const sizes = [
        {
            value: 'SOLO',
            label: `Just me`
        }, {
            value: 'SMALL',
            label: '2-5 people'
        }, {
            value: 'MEDIUM',
            label: `6-20 people`
        }, {
            value: `LARGE`,
            label: `20+ people`
        }
    ]

    useEffect(() => {
        if (isActiveStep) {
            setTimeout(() => {
                refreshSlider()
            }, 1000)
        }
    }, [isActiveStep])

    useEffect(() => {
        if (accountData && userData && !isSecondaryAccount) {
            const {
                size,
                industry,
                name,
                authorised_domain,
                id
            } = accountData

            setFormValues({
                accountName: name,
                size,
                industry,
                allowDomainSuggestion: !!authorised_domain,
                id
            })
        }
    }, [])

    const getEmailDomain = () => {
        const _userData = currentUserRef.current

        const { email } = _userData

        return email.replace(/.*@/, '')
    }

    const domainSuggestionToShow = (() => {
        let shouldShow = null

        const _userData = currentUserRef.current

        if (_userData) {
            const { email } = _userData

            const emailDomain = getEmailDomain()

            shouldShow = emailDomain

            let blacklistedDomainPatterns = [
                "@gmail.",
                "@yahoo.",
                "@live.",
                "@msn.",
                "@outlook.",
                "@hotmail.",
                "@bigpond.",
                "@mail.",
                "@gmx.",
                "@icloud.",
                "@aol.",
                "@yandex.",
                '@me.'
            ]

            blacklistedDomainPatterns.forEach(pattern => {
                if (email.toLowerCase().includes(pattern)) {
                    shouldShow = null
                }
            })
        }

        return shouldShow
    })()

    const formRows = [
        [
            {
                placeholder: 'Organization name',
                key: 'accountName',
                type: 'text',
            }
        ], [
            {
                placeholder: 'Team size',
                key: 'size',
                type: 'select',
                props: {
                    options: sizes
                }
            }
        ], [
            {
                placeholder: 'Industry',
                key: 'industry',
                type: 'select',
                subtitle: `We'll use this to create an example project for you.`,
                props: {
                    options: industries
                }
            }
        ], [
            {
                type: 'checkbox',
                key: 'allowDomainSuggestion',
                props: {
                    label: `Allow anyone with an <a>@${domainSuggestionToShow}</a> domain to join?`
                },
                hidden: !domainSuggestionToShow
            }
        ]
    ]

    const handleError = message => {
        setFormError(message)

        setTimeout(() => {
            refreshSlider()
        }, 350)

        setTimeout(() => {
            setFormError(null)

            setTimeout(() => {
                refreshSlider()
            }, 350)
        }, 3200)
    }

    const handleSubmit = async e => {
        e.preventDefault()

        const domain = getEmailDomain()
        const _formValues = formValuesRef.current
        const _currentUser = currentUserRef.current

        const accountSchema = yup.object().shape({
            accountName: yup.string().required('Please enter an organization name'),
            industry: yup.string().required(`Please choose an industry.`),
            size: yup.string().required(`Please choose a team size`)
        })

        const emailSchema = yup.object().shape({
            testEmail: yup.string().email('That domain doesn\'t seem valid.').required()
        })

        let requestData = {
            ..._formValues,
            domain,
            user_id: _currentUser.id,
            isSecondaryAccount,
            appSumoUsers: (appSumoUsers && appSumoUsers !== 'TAKEN') ? appSumoUsers : null,
            appSumoCode: appSumoCode || null 
        }

        // Run input fields against schema
        try {
            await accountSchema.validate({ ..._formValues })
        } catch (error) {
            return handleError(error.message)
        }

        // create a 'valid' email from the domain, to let yup validate.
        if (formValues.allowDomainSuggestion) {
            const testDomain = `a@${domain}`

            try {
                await emailSchema.validate({ testEmail: testDomain })
            } catch (error) {
                return handleError('That domain doesn\'t seem valid')
            }
        } else {
            delete requestData['domain']
        }

        setIsLoading(true)

        const isNew = !_formValues.id

        saveAccount(requestData, (error, response) => {
            if (error) {
                console.log(error)
                return handleError(error.message)
            }

            if (response.status === 200) {
                const os = getMobileOperatingSystem()
                const isIos = os && os === 'iOS'

                const shouldSkipBilling = (isIos && isCordova) || (appSumoUsers && appSumoUsers !== 'TAKEN')

                setTimeout(() => {
                    setIsLoading(false)
                    setStep(shouldSkipBilling ? 4 : 3)
                },shouldSkipBilling ? 300 : 0)
                
                if(shouldSkipBilling){
                    history.push('/' + response.data.slug)
                }

                logAnalyticsEvent({
                    category: 'Registration',
                    action: 'Completed Step',
                    label: '2 - Account Details'
                })

                if (!isSecondaryAccount) {
                    updateUserSubscriber({
                        Industry: requestData.industry,
                        TeamSize: requestData.size,
                        FirstOrganisationName: requestData.accountName,
                    })
                }

                if (isNew && loginCredentials) {
                    loginUser(loginCredentials, _ => {})
                }
            }
        })
    }

    const handleChange = (key, value) => {
        const _formValues = copy(formValues)

        _formValues[key] = value

        setFormValues(_formValues)
    }

    return (
        <React.Fragment>
            <Form
                rows={formRows}
                values={formValues}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                isLoading={isLoading}
                error={formError}
                isFocused={isActiveStep}
                buttonPrimary={{
                    label: 'Save details',
                    icon: 'tick'
                }}
                buttonLow={isSecondaryAccount ? {
                    label: `Go back`,
                    callback: closeCallback
                } : undefined}
            />
        </React.Fragment>
    )
}

const mapStateToProps = state => {
    return {
        userData: getCurrentUser(state, true),
        accountData: getCurrentAccount(state),
    }
}

export default withRouter(connect(mapStateToProps, {
    saveAccount,
    loginUser,
    updateUserSubscriber
})(Account))


// WEBPACK FOOTER //
// ./src/components/auth/register-modal/account.jsx